/**
 * @file facetsHelpers.js
 * @description Utility functions for parsing and extracting data from raw XAPI facets.
 * These helpers handle the complexity of nested facet structures (like Size groups)
 * and normalize them for easier consumption by the UI.
 */

import { DELIVERY_AND_PICKUP_V2 } from '~/features/FilteringFacets/constants/delivery-and-pickup';
import { GetItCopy } from '~/features/FilteringFacets/types/delivery-pickup-info';
import { BT_DELIVERY_PREFIX } from '~/utils/constants.js';

export const DEFAULT_SELECTION_ORDER = 0;
/**
 * Parses raw selected facets from XAPI into a lookup-friendly structure.
 *
 * @example Visual Representation of Return:
 * {
 *   filters: { SIZE: ['S', 'M'], COLOR: ['Red'] },
 *   order: ['SIZE', 'COLOR'],
 *   orderMap: { SIZE: 0, COLOR: 1 }
 * }
 *
 * @param {Object} selectedFacets - Raw facets from XAPI
 * @returns {Object} Parsed filter object
 */
export function parseFilters(selectedFacets) {
  const order = Object.keys(selectedFacets);
  const orderMap = order.reduce((acc, key, i) => {
    acc[key.toUpperCase()] = i;
    return acc;
  }, {});
  return { filters: selectedFacets, order, orderMap };
}

export function updateMultiSelectRange(facet, metaObj, transformedFacet) {
  if (facet.facetType === 'MULTISELECTRANGE') {
    const selected = metaObj?.selected?.find((selectFacet) => selectFacet?.name === facet?.name);
    const firstVal = selected?.values[0];
    const isCustomValue = facet?.values?.find((val) => val?.value === firstVal?.value) === undefined;
    if (firstVal && isCustomValue) {
      transformedFacet?.values?.push({
        label: `Custom: ${firstVal?.displayName}`,
        displayName: `Custom: ${firstVal?.value}`,
        value: firstVal?.value,
        selected: true,
      });
      if (!transformedFacet?.selectedValues?.length) transformedFacet?.selectedValues.push(firstVal.value);
    }
  }
}

export function getSelectedDnfValues(filtersRef) {
  const selectedValues = [];
  if (filtersRef.value?.filters?.UPC_SDD_PURCHASABLE) {
    selectedValues.push(`SDD=${filtersRef.value.filters.UPC_SDD_PURCHASABLE}`);
  }
  if (filtersRef.value?.filters?.UPC_BOPS_PURCHASABLE) {
    selectedValues.push(`BOPS=${filtersRef.value.filters.UPC_BOPS_PURCHASABLE}`);
  }
  return selectedValues;
}

export function getSelectedBTDeliveryValues(filtersRef) {
  const selectedValues = [];
  if (filtersRef.value?.filters?.UPC_DELIVERABLE) {
    selectedValues.push(`DELIVERABLE=${filtersRef.value.filters.UPC_DELIVERABLE}`);
  }
  return selectedValues;
}

/**
 * Extracts selected values from a MULTISELECTBUTTON facet (typically Size).
 * Handles both flat and grouped (hierarchical) children.
 *
 * @example Visual Representation of Return (Grouped):
 * [
 *   { name: 'Regular', selectedValues: ['S', 'M'] },
 *   { name: 'Petite', selectedValues: ['P/S'] }
 * ]
 *
 * @example Visual Representation of Return (Flat):
 * ['Red', 'Blue']
 *
 * @param {Object} facet - The raw facet object
 * @returns {Array<string|Object>} Selected values or objects
 */
export function getSelectedSizeFacet(facet) {
  const { children, values } = facet;

  if (!children) {
    return (values || []).filter((v) => v.selected).map((v) => v.value);
  }

  return children.flatMap((item) => {
    if (item.children) {
      return item.children
        .filter((child) => child.selectedCount > 0)
        .map((child) => ({
          name: child.name,
          selectedValues: (child.values || []).filter((v) => v.selected || v.personal).map((v) => v.originalVal || v.value),
        }));
    }

    if (item.selectedCount > 0) {
      return [
        {
          name: item.name,
          selectedValues: (item.values || []).filter((v) => v.selected || v.personal).map((v) => v.value),
        },
      ];
    }

    return [];
  });
}

export function getSelectedValues(facet, filtersRef) {
  if (facet.facetType === 'MULTISELECTBUTTON') {
    return getSelectedSizeFacet(facet);
  }
  // TODO remove when Get It today is scaled
  if (['DELIVERY_AND_PICKUP', DELIVERY_AND_PICKUP_V2].includes(facet.facetType)) {
    return getSelectedDnfValues(filtersRef);
  }
  if (facet.facetType === 'BT_DELIVERY') {
    return getSelectedBTDeliveryValues(filtersRef);
  }
  return [...(filtersRef.value.filters[facet.name] || [])];
}

export function getSelectedSizes(meta) {
  return meta?.selected ? meta.selected.filter((item) => item.name === 'SIZE_TYPE') : [];
}

const DNF_DISPLAY_NAME = {
  mcom: 'Delivery & Pickup',
  bcom: 'Pickup & Delivery',
};

export function buildDeliveryAndPickupFacet({ facets, alwaysDropdown = false, filters = {}, getItTodayEnabled = false }) {
  const dnfDislayName = getItTodayEnabled ? GetItCopy.getItToday : DNF_DISPLAY_NAME[import.meta.env.VITE_ENTRY_POINT];
  const facetType = getItTodayEnabled ? DELIVERY_AND_PICKUP_V2 : 'DELIVERY_AND_PICKUP';

  const dnpMap = {
    SDD: Array.isArray(filters?.UPC_SDD_PURCHASABLE) ? filters.UPC_SDD_PURCHASABLE[0] : '',
    BOPS: Array.isArray(filters?.UPC_BOPS_PURCHASABLE) ? filters.UPC_BOPS_PURCHASABLE[0] : '',
  };

  const dnpValues = [
    {
      url: '/Upc_sdd_purchasable',
      value: `SDD=${dnpMap.SDD}`,
    },
    {
      url: '/Upc-bops-purchasable',
      value: `BOPS=${dnpMap.BOPS}`,
    },
  ];

  if (getItTodayEnabled) {
    dnpValues.reverse();
  }

  const dnfFacet = {
    selectedValues: [],
    name: facetType,
    displayName: dnfDislayName,
    values: [...dnpValues],
    facetType,
    collapsed: true,
    indexable: true,
    alwaysDropdown,
    selectionOrder: -1,
  };
  return [dnfFacet, ...facets];
}

export function addBTDelivery(facetsArray, alwaysDropdown = true, isDeliveryAvailable, filters) {
  let dArray = [];
  if (isDeliveryAvailable) {
    dArray = [
      {
        name: 'UPC_DELIVERABLE',
        label: 'UPC_DELIVERABLE',
        selectedValues: filters.UPC_DELIVERABLE?.[0] ? [`${BT_DELIVERY_PREFIX}${filters.UPC_DELIVERABLE[0]}`] : [],
        displayName: 'Delivery',
        values: [
          {
            url: '/Upc_deliverable',
            value: `DELIVERABLE=${filters.UPC_DELIVERABLE || ''}`,
          },
        ],
        facetType: 'BT_DELIVERY',
        collapsed: true,
        indexable: true,
        alwaysDropdown,
      },
    ];
  }
  return [...dArray, ...facetsArray];
}

export function sortFacets(_facets) {
  return _facets.sort((a, b) => {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  });
}
